import { ServiceType } from "@bufbuild/protobuf";
import { createGrpcWebTransport } from "@connectrpc/connect-web";
import {
  Code,
  ConnectError,
  createClient,
  Interceptor,
  Transport,
} from "@connectrpc/connect";
import { createQueryService } from "@connectrpc/connect-query";
import { useMemo } from "react";
import { toast } from "react-toastify";
import { MyExperiment } from "@marketplace/myexperiment-connect/myexperiment_connect";
import { MyEvent } from "@marketplace/myevent-connect/myevent_connect";
import { getDeviceIdLocalStorage } from "utils/device";

type ServiceApiName = "rhino" | "gnome" | "kakapo" | "hydra";

const setExperimentHeaders: (deviceId: string) => Interceptor =
  (deviceId) => (next) => async (req) => {
    req.header.set("x-tenant-id", process.env.REACT_APP_NAU_TENANT_ID || "");
    req.header.set("x-referer", window.location.hostname);
    req.header.set("x-device-id", deviceId);
    return await next(req);
  };

export const createMyExperimentClient = (apiDomain: string, deviceId: string) =>
  createClient(
    MyExperiment,
    createGrpcWebTransport({
      baseUrl: `https://${apiDomain}`,
      interceptors: [setExperimentHeaders(deviceId), loggerWithErrorHandler()],
      jsonOptions: { ignoreUnknownFields: true },
    }),
  );

export const createMyEventClient = (apiDomain: string, token: string, silent = false) =>
  createClient(
    MyEvent,
    createGrpcWebTransport({
      baseUrl: `https://${apiDomain}/api/turkey`,
      interceptors: [setEventHeaders(), setToken(token), loggerWithErrorHandler(silent)],
      jsonOptions: { ignoreUnknownFields: true },
    }),
  );

const setEventHeaders: () => Interceptor = () => (next) => async (req) => {
  req.header.set("x-tenant-id", process.env.REACT_APP_NAU_TENANT_ID || "");
  req.header.set("x-referer", window.location.hostname);
  const deviceId = getDeviceIdLocalStorage();
  if (deviceId) req.header.set("x-device-id", deviceId);
  return await next(req);
};

export const getToken = () => localStorage.getItem("token") || "";

export const uniconTransport = () => {
  return createGrpcWebTransport({
    baseUrl: `https://unicorn.n11s.io`,
    interceptors: [setToken(getToken()), loggerWithErrorHandler()],
  });
};

/**
 * Get a promise client for the given service.
 */
export function useClient<T extends ServiceType>(
  service: T,
  transport?: Transport
) {
  // We memoize the client, so that we only create one instance per service.
  return useMemo(
    () => createQueryService({ service, transport }),
    [service, transport]
  );
}

const setToken: (token?: string) => Interceptor = (token?: string) => {
  return (next) => async (req) => {
    if (token) req.header.set("Authorization", "Bearer " + token);
    return await next(req);
  };
};

const loggerWithErrorHandler: (silent?: boolean) => Interceptor = (silent = false) => {
  return (next) => async (req) => {
    console.log(
      `%c gRPCClientRequest -> [${req.service.typeName} > ${req.method.name}] -> REQUEST:`,
      "background-color: #deeb34; color: #000; font-size: 14px",
      req
    );
    try {
      const res = await next(req);

      console.log(
        `%c>>>>> gRPCClientResponse -> [${req.service.typeName} > ${req.method.name}] -> SUCCESS:`,
        "background-color: #23d947; color: #000; font-size: 14px",
        res
      );

      return res;
    } catch (err) {
      const connectErr = ConnectError.from(err);
      switch (connectErr?.code) {
        case Code.Unauthenticated:
          console.log(
            `%c>>>>> gRPCClientResponse -> [${req.service.typeName} > ${req.method.name}] -> ERROR -> UNAUTHENTICATED: `,
            "background-color: #c0392b; color: #000; font-size: 14px",
            err
          );

          // window.location.href = "/sign-in";
          localStorage.removeItem("token");

          break;
        default:
          console.log(
            `%c>>>>> gRPCClientResponse  -> [${req.service.typeName}] > ${req.method.name}] -> ERROR: `,
            "background-color: #c0392b; color: #000; font-size: 14px",
            err
          );
          break;
      }

      if (!silent) toast.error(connectErr.message);
      return Promise.reject(connectErr);
    }
  };
};
